import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  TextField,
  FormControl,
  Typography,
  useMediaQuery,
  Autocomplete,
} from "@mui/material";
import { useFormik } from "formik";
import * as yup from "yup";
import Swal from "sweetalert2";
import Header from "../../../../components/Header";
import { tokens } from "../../../../theme";
import { useTheme } from "@mui/material";
import { generateAndExecuteMutation } from "../../../../data/Axios/DynamicService";
import { caseManagementUrl } from "../../../../config";
import { useDispatch } from "react-redux";
import { UpdateDataToStore } from "../../../../store/Actions/CustomAction";

const FileInstructionForm = (props) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const base_url = caseManagementUrl.uri;
  const dispatch = useDispatch();

  // Initialize states directly with arrays to avoid undefined options
  const [clientOptions, setClientOptions] = useState([]);
  const [instructionTypeOptions, setInstructionTypeOptions] = useState([]);
  const [instructionCategoryOptions, setInstructionCategoryOptions] = useState([]);
  const [receiverOptions, setReceiverOptions] = useState([]);
  const [currencyOptions, setCurrencyOptions] = useState([]);
  const [tatOptions, setTatOptions] = useState([]);
  const [priorityOptions, setPriorityOptions] = useState([]);
  const [notificationOptions, setNotificationOptions] = useState([]);

  // New state to track selected client (kept for any future use, but not tied to files now)
  const [selectedClient, setSelectedClient] = useState("");
  // State for displaying selected file name
  const [selectedFileName, setSelectedFileName] = useState("");

  // Ref for hidden file input
  const fileInputRef = useRef(null);

  const decodedToken = JSON.parse(localStorage.getItem("decodedToken"));
  const userIdFromSession = parseInt(decodedToken.Id);

  const initialValues = {
    date: props.data ? props.data.date : " ", // Updated to current date
    client: props.data ? props.data.client : "",
    file: props.data ? props.data.file : null, // Changed to null for File object
    instructionType: props.data ? props.data.instructionType : "",
    instructionCategory: props.data ? props.data.instructionCategory : " ",
    receiver: props.data ? props.data.receiver : "",
    currency: props.data ? props.data.currency : "",
    exchangeRate: props.data ? props.data.exchangeRate : "",
    tat: props.data ? props.data.tat : "",
    priority: props.data ? props.data.priority : "",
    amount: props.data ? props.data.amount : "",
    details: props.data ? props.data.details : "",
    notificationsTo: props.data ? props.data.notificationsTo : "",
  };

  // Set initial selectedClient for editing mode
  useEffect(() => {
    if (props.isEditing && props.data?.client) {
      setSelectedClient(props.data.client);
      // If editing and file is a string (existing), set display name
      if (props.data.file && typeof props.data.file === 'string') {
        setSelectedFileName(`Existing: ${props.data.file}`);
      }
    }
  }, [props.isEditing, props.data?.client, props.data?.file]);

  const validationSchema = yup.object().shape({
    date: yup.date().required("Date is required"),
    client: yup.string().required("Client is required"),
    file: yup
      .mixed()
      .required("Please select a file to upload!")
      .test("fileSize", "The file has exceeded the 50mb limit", value => {
        return value && value.size <= 52428800; // 50MB
      })
      .test("fileType", "Unsupported file format", value => {
        return value && ['image/jpeg', 'image/png', 'application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'].includes(value.type);
      }),
    instructionType: yup.string().required("Instruction Type is required"),
    instructionCategory: yup.string().required("Instruction Category is required"),
    receiver: yup.string().required("Receiver is required"),
    currency: yup.string().required("Currency is required"),
    exchangeRate: yup.number().required("Exchange Rate is required"),
    tat: yup.string().required("Turn-Around Time (TAT) is required"),
    priority: yup.string().required("Priority is required"),
    amount: yup.number().required("Amount is required"),
    details: yup.string().required("Details are required"),
    notificationsTo: yup.string().required("Notifications To is required"),
  });

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const instructionObject = {
        ...(props.isEditing && { id: props.data.id }),
        ...values,
        createdBy: userIdFromSession,
      };

      // If file is a File object, handle upload (example: convert to FormData if backend expects it)
      // Adjust based on your API; here assuming mutation handles File directly
      // const formData = new FormData();
      // Object.keys(instructionObject).forEach(key => {
      //   if (key === 'file' && instructionObject[key] instanceof File) {
      //     formData.append('file', instructionObject[key]);
      //   } else {
      //     formData.append(key, instructionObject[key]);
      //   }
      // });
      // const response = await generateAndExecuteMutation(..., formData, ...);

      const response = await generateAndExecuteMutation(
        props.isEditing ? "updateFileInstructionDynamic" : "createFileInstructionDynamic",
        instructionObject,
        props.isEditing ? "fileInstruction" : "newFileInstruction",
        base_url
      );

      if (Object.values(response)[0]) {
        props.onClose();
        props.onAction();
        Swal.fire({
          icon: "success",
          title: "Success!",
          text: `File Instruction ${props.isEditing ? "updated" : "created"} successfully.`,
        });
        dispatch(UpdateDataToStore("REFRESH_FILE_INSTRUCTIONS_TABLE", "success"));
      }

      setSubmitting(false);
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: "An error occurred. Please try again later.",
      });
      dispatch(UpdateDataToStore("REFRESH_FILE_INSTRUCTIONS_TABLE", "error"));
      setSubmitting(false);
    }
  };

  // Use useFormik hook
  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: handleSubmit,
  });

  const handleClose = () => props.onClose();

  const isNonMobile = useMediaQuery("(min-width:900px)");

  const inputStyle = {
    color: "#fff",
    "& .MuiInputLabel-root": { color: "#ccc" },
    "& .MuiInputBase-input": { color: "#fff" },
    "& .MuiInputBase-input::placeholder": { color: "#999" },
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#333",
      "& fieldset": { borderColor: "#555" },
      "&:hover fieldset": { borderColor: "#777" },
      "&.Mui-focused fieldset": { borderColor: "#0d47a1" },
    },
    "& .MuiAutocomplete-input": { color: "#fff" },
  };

  const reducedHeightStyle = {
    ...inputStyle,
    "& .MuiInputBase-root": { height: "40px" },
  };

  const mediumInputStyle = {
    ...reducedHeightStyle,
    width: "120px",
  };

  const smallInputStyle = {
    ...reducedHeightStyle,
    width: "60px",
  };

  // Helper to handle Autocomplete change for Formik
  const handleAutocompleteChange = (name) => (event, value) => {
    formik.handleChange({
      target: { name, value: value ? value.value : "" }
    });
  };

  // Helper to handle client change
  const handleClientChange = (event, value) => {
    const newClientValue = value ? value.value : "";
    formik.setFieldValue('client', newClientValue);
    setSelectedClient(newClientValue);
  };

  // Handle file selection
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      formik.setFieldValue('file', file);
      setSelectedFileName(file.name);
      // Trigger validation
      formik.validateField('file');
    }
  };

  // Trigger file input click
  const handleFileClick = () => {
    fileInputRef.current?.click();
  };

  // Clear file
  const handleClearFile = (e) => {
    e.stopPropagation();
    formik.setFieldValue('file', null);
    setSelectedFileName("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <Box
      m="5px"
      sx={{ 
        backgroundColor: "#292626ff", 
        borderRadius: 2, 
        boxShadow: 1, 
        border: "1px solid #333", 
        p: 3,
        color: "#fff",
        minHeight: 400,
      }}
    >
      <Header
        title={props.isEditing ? "Edit File Instruction" : "New Instruction"}
        subtitle={props.isEditing ? "Edit an Existing File Instruction" : "Create a New File Instruction"}
      />
      <form onSubmit={formik.handleSubmit}>
        <Box
          display="grid"
          gridTemplateColumns={isNonMobile ? "200px 1fr" : "1fr"}
          gap={2}
          alignItems="start"
        >
          <Typography sx={{ color: "#fff" }}>Date *</Typography>
          <TextField
            fullWidth
            type="date"
            name="date"
            value={formik.values.date}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.date && Boolean(formik.errors.date)}
            helperText={formik.touched.date && formik.errors.date}
            InputLabelProps={{ shrink: true }}
            sx={mediumInputStyle}
          />

          <Typography sx={{ color: "#fff" }}>Client *</Typography>
          <FormControl fullWidth error={formik.touched.client && Boolean(formik.errors.client)} sx={reducedHeightStyle}>
            <Autocomplete
              options={clientOptions || []} // Safeguard
              getOptionLabel={(option) => option.label}
              value={clientOptions.find(opt => opt.value === formik.values.client) || null}
              onChange={handleClientChange}
              onBlur={formik.handleBlur}
              renderInput={(params) => (
                <TextField
                  {...params}
                  name="client"
                  error={formik.touched.client && Boolean(formik.errors.client)}
                  helperText={formik.touched.client && formik.errors.client}
                  placeholder="Type to search clients..."
                  sx={inputStyle}
                />
              )}
            />
          </FormControl>

          <Typography sx={{ color: "#fff" }}>File *</Typography>
          <FormControl fullWidth error={formik.touched.file && Boolean(formik.errors.file)} sx={reducedHeightStyle}>
            <input
              ref={fileInputRef}
              type="file"
              onChange={handleFileSelect}
              accept="application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/*"
              style={{ display: "none" }}
            />
            <TextField
              fullWidth
              name="file"
              value={selectedFileName}
              onClick={handleFileClick}
              onBlur={formik.handleBlur}
              error={formik.touched.file && Boolean(formik.errors.file)}
              helperText={formik.touched.file && formik.errors.file}
              placeholder={selectedClient ? "Click to upload a file..." : "Select client first to upload files..."}
              sx={inputStyle}
              InputProps={{
                readOnly: true,
                endAdornment: selectedFileName ? (
                  <Button size="small" onClick={handleClearFile} sx={{ color: "#fff", borderColor: "#555" }}>
                    Clear
                  </Button>
                ) : null,
              }}
            />
          </FormControl>

          <Typography sx={{ color: "#fff" }}>Instruction Type *</Typography>
          <FormControl fullWidth error={formik.touched.instructionType && Boolean(formik.errors.instructionType)} sx={reducedHeightStyle}>
            <Autocomplete
              options={instructionTypeOptions || []} // Safeguard
              getOptionLabel={(option) => option.label}
              value={instructionTypeOptions.find(opt => opt.value === formik.values.instructionType) || null}
              onChange={handleAutocompleteChange('instructionType')}
              onBlur={formik.handleBlur}
              renderInput={(params) => (
                <TextField
                  {...params}
                  name="instructionType"
                  error={formik.touched.instructionType && Boolean(formik.errors.instructionType)}
                  helperText={formik.touched.instructionType && formik.errors.instructionType}
                  placeholder="Type to search instruction types..."
                  sx={inputStyle}
                />
              )}
            />
          </FormControl>

          <Typography sx={{ color: "#fff" }}>Instruction Category *</Typography>
          <FormControl fullWidth error={formik.touched.instructionCategory && Boolean(formik.errors.instructionCategory)} sx={reducedHeightStyle}>
            <Autocomplete
              options={instructionCategoryOptions || []} // Safeguard
              getOptionLabel={(option) => option.label}
              value={instructionCategoryOptions.find(opt => opt.value === formik.values.instructionCategory) || null}
              onChange={handleAutocompleteChange('instructionCategory')}
              onBlur={formik.handleBlur}
              renderInput={(params) => (
                <TextField
                  {...params}
                  name="instructionCategory"
                  error={formik.touched.instructionCategory && Boolean(formik.errors.instructionCategory)}
                  helperText={formik.touched.instructionCategory && formik.errors.instructionCategory}
                  placeholder="Type to search instruction categories..."
                  sx={inputStyle}
                />
              )}
            />
          </FormControl>

          <Typography sx={{ color: "#fff" }}>Receiver *</Typography>
          <FormControl fullWidth error={formik.touched.receiver && Boolean(formik.errors.receiver)} sx={reducedHeightStyle}>
            <Autocomplete
              options={receiverOptions || []} // Safeguard
              getOptionLabel={(option) => option.label}
              value={receiverOptions.find(opt => opt.value === formik.values.receiver) || null}
              onChange={handleAutocompleteChange('receiver')}
              onBlur={formik.handleBlur}
              renderInput={(params) => (
                <TextField
                  {...params}
                  name="receiver"
                  error={formik.touched.receiver && Boolean(formik.errors.receiver)}
                  helperText={formik.touched.receiver && formik.errors.receiver}
                  placeholder="Type to search receivers..."
                  sx={inputStyle}
                />
              )}
            />
          </FormControl>

          <Box sx={{ gridColumn: "1 / -1", display: "grid", gridTemplateColumns: "200px 90px 1fr 120px", gap: 1, alignItems: "center" }}>
            <Typography sx={{ color: "#fff" }}>Currency *</Typography>
            <FormControl error={formik.touched.currency && Boolean(formik.errors.currency)} sx={smallInputStyle}>
              <Autocomplete
                options={currencyOptions || []} // Safeguard
                getOptionLabel={(option) => option.label}
                value={currencyOptions.find(opt => opt.value === formik.values.currency) || null}
                onChange={handleAutocompleteChange('currency')}
                onBlur={formik.handleBlur}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    name="currency"
                    error={formik.touched.currency && Boolean(formik.errors.currency)}
                    helperText={formik.touched.currency && formik.errors.currency}
                    placeholder="Type currency..."
                    sx={inputStyle}
                  />
                )}
              />
            </FormControl>
            <Typography sx={{ color: "#fff" }}>Exchange Rate *</Typography>
            <TextField
              type="number"
              name="exchangeRate"
              value={formik.values.exchangeRate}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.exchangeRate && Boolean(formik.errors.exchangeRate)}
              helperText={formik.touched.exchangeRate && formik.errors.exchangeRate}
              sx={mediumInputStyle}
            />
          </Box>

          <Typography sx={{ color: "#fff" }}>Turn-Around Time (TAT) *</Typography>
          <FormControl fullWidth error={formik.touched.tat && Boolean(formik.errors.tat)} sx={reducedHeightStyle}>
            <Autocomplete
              options={tatOptions || []} // Safeguard
              getOptionLabel={(option) => option.label}
              value={tatOptions.find(opt => opt.value === formik.values.tat) || null}
              onChange={handleAutocompleteChange('tat')}
              onBlur={formik.handleBlur}
              renderInput={(params) => (
                <TextField
                  {...params}
                  name="tat"
                  error={formik.touched.tat && Boolean(formik.errors.tat)}
                  helperText={formik.touched.tat && formik.errors.tat}
                  placeholder="Type TAT..."
                  sx={inputStyle}
                />
              )}
            />
          </FormControl>

          <Typography sx={{ color: "#fff" }}>Priority *</Typography>
          <FormControl fullWidth error={formik.touched.priority && Boolean(formik.errors.priority)} sx={reducedHeightStyle}>
            <Autocomplete
              options={priorityOptions || []} // Safeguard
              getOptionLabel={(option) => option.label}
              value={priorityOptions.find(opt => opt.value === formik.values.priority) || null}
              onChange={handleAutocompleteChange('priority')}
              onBlur={formik.handleBlur}
              renderInput={(params) => (
                <TextField
                  {...params}
                  name="priority"
                  error={formik.touched.priority && Boolean(formik.errors.priority)}
                  helperText={formik.touched.priority && formik.errors.priority}
                  placeholder="Type priority..."
                  sx={inputStyle}
                />
              )}
            />
          </FormControl>

          <Typography sx={{ color: "#fff" }}>Amount *</Typography>
          <TextField
            fullWidth
            type="number"
            name="amount"
            value={formik.values.amount}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.amount && Boolean(formik.errors.amount)}
            helperText={formik.touched.amount && formik.errors.amount}
            sx={reducedHeightStyle}
          />

          <Typography sx={{ color: "#fff" }}>Details *</Typography>
          <TextField
            fullWidth
            multiline
            rows={2}
            name="details"
            value={formik.values.details}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.details && Boolean(formik.errors.details)}
            helperText={formik.touched.details && formik.errors.details}
            sx={inputStyle}
          />

          <Typography sx={{ color: "#fff" }}>Send Notifications To: *</Typography>
          <FormControl fullWidth error={formik.touched.notificationsTo && Boolean(formik.errors.notificationsTo)} sx={{ ...inputStyle, height: "50px" }}>
            <Autocomplete
              options={notificationOptions || []} // Safeguard
              getOptionLabel={(option) => option.label}
              value={notificationOptions.find(opt => opt.value === formik.values.notificationsTo) || null}
              onChange={handleAutocompleteChange('notificationsTo')}
              onBlur={formik.handleBlur}
              renderInput={(params) => (
                <TextField
                  {...params}
                  name="notificationsTo"
                  error={formik.touched.notificationsTo && Boolean(formik.errors.notificationsTo)}
                  helperText={formik.touched.notificationsTo && formik.errors.notificationsTo}
                  placeholder="Type to search users..."
                  sx={inputStyle}
                />
              )}
            />
          </FormControl>
        </Box>

        <Box display="flex" justifyContent="center" mt={3} gap={2}>
          <Button type="submit" variant="contained" sx={{ backgroundColor: "#4caf50", color: "white" }} disabled={formik.isSubmitting}>
            {formik.isSubmitting ? "Submitting..." : "Send Request"}
          </Button>
          <Button type="button" variant="outlined" onClick={handleClose} sx={{ color: "white", borderColor: "white" }}>
            Close
          </Button>
        </Box>
      </form>
    </Box>
  );
};

export default FileInstructionForm;